<template>
    <div>
        <div class="w-full">
            <div class="grid grid-cols-2 2xl:grid-cols-3 auto-cols-max text-gray-700 gap-1">
                <div class=" bg-gray-200 text-center relative z-10" :class="currentPriceClasses">
                    <div v-if="this.isIntervalCountdownActive" class="absolute h-full duration-500" :style="riderStyles" :class="riderClasses"></div>
                    <div class="p-2">
                        <span class="text-3xl">{{ runtime.current_price | toCurrency(2) }}</span><br/>
                        <span class="text-xs">{{$t('pages.auctions.control.components.auction_runtime_data.current_price')}}</span>
                    </div>
                </div>
                <div class=" bg-gray-200 text-center p-2">
                    <span class="text-3xl">{{ runtime.interval_duration }} <countdown @timerUpdate="updateSecondsRemaining" v-if="this.isIntervalCountdownActive" :updateAllowed="this.canUpdateCountdown" :active="this.isIntervalCountdownActive" :left="this.timeLeft">/ </countdown></span><br/>
                    <span class="text-xs">{{$t('pages.auctions.control.components.auction_runtime_data.interval')}}</span>
                </div>
                <div class=" bg-gray-200 text-center p-2">
                    <span class="text-3xl">{{ soldQuantity }}</span><br/>
                    <span class="text-xs">{{$t('pages.auctions.control.components.auction_runtime_data.bids')}}</span>
                </div>
                <div class=" bg-gray-200 text-center p-2">
                    <span class="text-3xl">{{ runtime.amount_left }}</span><br/>
                    <span class="text-xs">{{$t('pages.auctions.control.components.auction_runtime_data.amount_left')}}</span>
                </div>
                <div class=" bg-gray-200 text-center p-2">
                    <span class="text-3xl" :class="{ 'text-ezdRed': currentInstantDiscount > 0 }">{{ priceDropAmount | toCurrency(2) }}</span><br/>
                    <span class="text-xs">{{$t('pages.auctions.control.components.auction_runtime_data.price_drop_amount')}}</span>
                </div>
                <div class=" bg-gray-200 text-center p-2 col-span-2 2xl:col-span-3" v-if="runtime.stocks.children.length > 0">
                    <div class="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 auto-cols-max gap-2 my-2">
                        <div class="col-span-2 lg:col-span-3 2xl:col-span-4 text-left text-sm font-bold">Varianten</div>
                        <div class="flex justify-between items-center gap-2" v-for="variant in runtime.stocks.children" :key="variant.id">
                            <div class="text-sm font-bold" :class="calcVariantStyle(variant)">{{ variant.stock }}</div>
                            <div class="text-sm">{{ variant.label }}</div>
                        </div>
                    </div>
                </div>
                <div class=" bg-gray-200 text-center p-2 col-span-2 2xl:col-span-3" v-if="auction.bids?.length > 0">
                    <div class="grid grid-cols-2 2xl:grid-cols-3 auto-cols-max gap-2 my-2">
                        <div class="col-span-2 lg:col-span-3 2xl:col-span-4 text-left text-sm font-bold">Gebote ({{ auction.bids?.length ?? 0 }})</div>
                        <div class="flex justify-between items-center gap-2" v-for="bid in auction.bids" :key="bid.id">
                            <div class="text-sm text-left">
                                <div class="flex items-center justify-between font-bold">
                                    <span>{{ bid.customer_name ?? 'n.b.' }}</span>
                                    <svg v-if="!customerLocks.includes(bid.customer_id)" @click="lockCustomer(bid)" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-green-600 cursor-pointer">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                                    </svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" @click="unlockCustomer(bid)" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-red-400 cursor-pointer">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                                    </svg>
                                </div>
                                <div class="hidden 2xl:inline-block me-1">{{ bid.created_at | formatTime }} | </div>{{ bid.quantity }} Stck. | {{ bid.amount | toCurrency(2) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Countdown from "@/Shared/Countdown";
import AuctionState from "@/mixins/AuctionState";
export default {
    name: "Auctionruntime",
    mixins: [
        AuctionState
    ],
    components: {
        Countdown
    },
    props: {
        runtime: Object,
        auction: Object,
        latestSocketEvent: {
            type: String,
            default: null
        },
        customerLocks: {
            type: Array,
            default: []
        }
    },
    methods: {
        updateSecondsRemaining(remaining) {
            this.secondsRemaining = remaining;
        },
        calcVariantStyle(variant) {
          return {
              'text-green-500': variant.stock > 10,
              'text-orange-500': variant.stock <= 10 && variant.stock > 0,
              'text-red-500': variant.stock === 0
          }
        },
        lockCustomer(bid) {
            if (!this.customerLocks.includes(bid.customer_id)) {
                if (confirm('Möchten Sie den Kunden ' + bid.customer_name + ' wirklich sperren (auch für zukünftige Auktionen)?')) {
                    axios.post(route('customer.lock.from-bid', { bid: bid.id }))
                        .then((resp) => {
                            this.$inertia.reload({
                                only: ['customerLocks']
                            });
                        })
                        .catch((err) => {
                            console.error(err);
                        })
                }
            }
        },
        unlockCustomer(bid) {
            if (confirm('Möchten Sie den Kunden ' + bid.customer_name + ' wirklich entsperren?')) {
                axios.post(route('customer.unlock.from-bid', { bid: bid.id }))
                    .then((resp) => {
                        this.$inertia.reload({
                            only: ['customerLocks']
                        });
                    })
                    .catch((err) => {
                        console.error(err);
                    })
            }
        }
    },
    data() {
        return {
            secondsRemaining: 0
        }
    },
    computed: {
        soldQuantity: function() {
            return this.auction.bids.reduce((accumulator, bid) => {
                return accumulator + bid.quantity;
            }, 0);
        },
        canUpdateCountdown() {
            if (this.latestSocketEvent === null) {
                return true;
            }
            return ['AuctionStarted', 'AuctionPriceDrop', 'AuctionTrailing', 'AuctionStopped'].includes(this.latestSocketEvent);
        },
        timeLeft: function() {
            return {
                seconds: this.runtime.interval_time_remaining
            }
        },
        isIntervalCountdownActive: function() {
            return (parseInt(this.auction.state) === 3);
        },
        currentInstantDiscount: function() {
            if (this.runtime.context.ID) {
                return this.runtime.context.ID.amount;
            }
            return 0;
        },
        priceDropAmount: function() {
            if (this.currentInstantDiscount > 0) {
                return this.currentInstantDiscount + this.runtime.price_drop_amount;
            }
            return this.runtime.price_drop_amount;
        },
        riderWidth: function() {
            return this.secondsRemaining === 0 ? '100' : (Math.round((100 / this.runtime.interval_duration) * this.secondsRemaining));
        },
        riderStyles: function() {
            return {
                width: this.riderWidth + '%',
                'z-index': -5
            }
        },
        riderClasses: function() {
            return {
                'bg-gray-600 opacity-25': !this.isPriceTurboActive(this.runtime),
                'bg-red-600': this.isPriceTurboActive(this.runtime),
                'border-r-8 border-red-600': this.isPriceTurboInitiated(this.runtime)
            }
        },
        currentPriceClasses: function() {
            return {
                'text-white': this.isIntervalCountdownActive && this.isPriceTurboActive(this.runtime) && this.riderWidth > 40
            }
        }
    }
}
</script>
